import { useEffect, useState } from "react";
import axios from "axios";
import { Pause, Play } from "lucide-react";
import { API } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Equalizer } from "@/components/Equalizer";
import { Skeleton } from "@/components/ui/skeleton";

export const useChannels = () => {
  const [channels, setChannels] = useState(null);
  useEffect(() => { axios.get(`${API}/channels`).then((r) => setChannels(r.data)).catch(() => setChannels([])); }, []);
  return channels;
};

const ChannelCard = ({ ch, index }) => {
  const { channel, playing, playChannel, toggle } = usePlayer();
  const active = channel?.slug === ch.slug;
  const onClick = () => (active ? toggle() : playChannel(ch));
  return (
    <button
      data-testid={`channel-card-${ch.slug}`}
      onClick={onClick}
      style={{ animationDelay: `${index * 60}ms` }}
      className={`fade-up group relative aspect-[4/5] overflow-hidden rounded-xl border text-left transition-[border-color,transform] duration-300 hover:-translate-y-1 ${active ? "border-[#FF4500]/70" : "border-white/10"}`}
    >
      <img src={ch.image} alt={ch.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/50 to-transparent" />
      <div className="absolute left-4 top-4 font-mono text-[10px] tracking-[0.25em] text-white/70">24/7 · {String(index + 1).padStart(2, "0")}</div>
      {ch.live_show && <div data-testid={`channel-live-show-${ch.slug}`} className="absolute right-3 top-3 max-w-[60%] truncate rounded-full bg-[#FF4500] px-2.5 py-1 font-mono text-[10px] text-white">EN VIVO · {ch.live_show.title}</div>}
      <div className="absolute bottom-0 w-full p-3 sm:p-5">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h3 className="break-words font-heading text-base font-extrabold leading-tight text-white sm:text-2xl">{ch.name}</h3>
            <p className="mt-1 line-clamp-2 hidden text-xs text-slate-300 sm:block">{ch.description}</p>
            <p className="mt-2 font-mono text-[10px] tracking-widest text-slate-400">{ch.track_count} CANCIONES</p>
          </div>
          <span className={`flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full transition-colors ${active ? "bg-[#FF4500]" : "bg-white/10 group-hover:bg-[#FF4500]"}`}>
            {active && playing ? <Pause className="h-4 w-4 fill-white text-white" /> : <Play className="ml-0.5 h-4 w-4 fill-white text-white" />}
          </span>
        </div>
        {active && playing && <div className="mt-3 flex items-center gap-2 font-mono text-[10px] tracking-widest text-[#FF7A45]"><Equalizer /> SONANDO</div>}
      </div>
    </button>
  );
};

export const ChannelGrid = () => {
  const channels = useChannels();
  if (!channels) return <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="aspect-[4/5] rounded-xl bg-white/5" />)}</div>;
  return (
    <div data-testid="channel-grid" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {channels.map((ch, i) => <ChannelCard key={ch.id} ch={ch} index={i} />)}
    </div>
  );
};
